import { z } from "zod";

import { SUPPORTED_HARDWARE, type HardwareId } from "../devices/hardware";

/**
 * Public feature flags the SPA may read without a session.
 *
 * Only the safe subset: hardware visibility for catalogue, device forms and
 * marketing. Internal flags such as `redirectAllEmailTo` stay on the server.
 *
 * Always a complete map of every registry id after defaults (ADR-057).
 */

const enabledHardwareMapShape = Object.fromEntries(
  SUPPORTED_HARDWARE.map((hardware) => [hardware.id, z.boolean()]),
) as { [K in HardwareId]: z.ZodBoolean };

export const enabledHardwareMapSchema = z.object(enabledHardwareMapShape).strict();

export type EnabledHardwareMap = z.infer<typeof enabledHardwareMapSchema>;

/** GET /api/v1/features */
export const featuresResponseSchema = z.object({
  enabledHardware: enabledHardwareMapSchema,
  /**
   * Whether `POST /integrations/lorawan/simulate` is accepting traffic.
   * Always false in production; the SPA uses this to hide the inject button
   * rather than offering a control that only fails with 403 (ADR-087).
   */
  lorawanSimulatorEnabled: z.boolean(),
  /**
   * Whether devices and gateways registered here are pushed to The Things
   * Stack (ADR-089). When true the SPA hides the Network Server id fields,
   * which the API derives, and asks for the AppKey to forward.
   */
  networkProvisioningEnabled: z.boolean(),
});

export type FeaturesResponse = z.infer<typeof featuresResponseSchema>;
